import { useCallback, useEffect, useRef, useState } from "react"

// 1:1 WebRTC voice calls over the messenger signaling relay.
// Global chat is never callable — the relay rejects it and the UI never offers it.
//
// State machine (status):
//   idle      — no call
//   calling   — outgoing offer sent, waiting for the peer to pick up
//   ringing   — incoming offer received, waiting for us to accept/decline
//   connecting— answer exchanged, ICE still negotiating
//   in-call   — media connected
// Transient outcomes surface via `note` (declined / busy / failed / ended).

const POLL_MS = 1000
const newCallId = () => Math.random().toString(36).slice(2) + Date.now().toString(36)

async function fetchIceServers(authFetch) {
  try {
    const r = await authFetch("/api/messenger/call/config/")
    const d = await r.json()
    if (Array.isArray(d.ice_servers) && d.ice_servers.length) {
      console.debug("[call-diag] ICE servers", { ok: r.ok, status: r.status, servers: d.ice_servers })
      return d.ice_servers
    }
    console.debug("[call-diag] ICE servers response missing ice_servers — falling back to STUN-only", { ok: r.ok, status: r.status, body: d })
  } catch (err) { console.debug("[call-diag] fetchIceServers FAILED — falling back to STUN-only", err) }
  return [{ urls: ["stun:stun.l.google.com:19302"] }]
}

export function useVoiceCall(username, authFetch) {
  // TODO: Run 2–4-client group/screen-share browser E2E after the next frontend rebuild.
  const [status, setStatus] = useState("idle")
  const [peer, setPeer] = useState(null)        // the other user's username
  const [muted, setMuted] = useState(false)
  const [localScreenShares, setLocalScreenShares] = useState([])
  const [remoteScreenShares, setRemoteScreenShares] = useState([])
  const [groupCall, setGroupCall] = useState({ status: "idle", group: null, peers: [], pending: [] })
  const [groupMuted, setGroupMuted] = useState(false)
  const [note, setNote] = useState(null)        // { kind, peer } transient outcome

  const pcRef = useRef(null)
  const localStreamRef = useRef(null)
  const remoteAudioRef = useRef(null)
  const callIdRef = useRef(null)
  const peerRef = useRef(null)
  const pendingIceRef = useRef([])              // ICE arriving before remoteDescription
  const incomingOfferRef = useRef(null)         // { sdp } awaiting accept
  const statusRef = useRef("idle")
  const screenSharesRef = useRef(new Map())
  const groupCallRef = useRef({ status: "idle", group: null, peers: [], pending: [] })
  const groupCallIdRef = useRef(null)
  const groupPcRefs = useRef(new Map())
  const groupAudioRefs = useRef(new Map())
  const groupIceRef = useRef(new Map())
  const groupStreamRef = useRef(null)

  const setGroupBoth = (next) => { groupCallRef.current = next; setGroupCall(next) }

  const setStatusBoth = (s) => { statusRef.current = s; setStatus(s) }

  const ensureRemoteAudio = () => {
    if (!remoteAudioRef.current) {
      const el = document.createElement("audio")
      el.autoplay = true
      el.style.display = "none"
      document.body.appendChild(el)
      remoteAudioRef.current = el
    }
    return remoteAudioRef.current
  }

  const postSignal = useCallback(async (to, kind, payload) => {
    const response = await authFetch("/api/messenger/call/signal/", {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({ call_id: callIdRef.current, from: username, to, kind, payload }),
    })
    if (!response.ok) throw new Error(`Call signal failed: ${response.status}`)
    return response
  }, [username, authFetch])

  const cleanup = useCallback(() => {
    if (pcRef.current) { try { pcRef.current.close() } catch { /* noop */ } pcRef.current = null }
    if (localStreamRef.current) {
      localStreamRef.current.getTracks().forEach(t => t.stop())
      localStreamRef.current = null
    }
    screenSharesRef.current.forEach(({ stream }) => stream.getTracks().forEach(t => t.stop()))
    screenSharesRef.current.clear()
    if (remoteAudioRef.current) { remoteAudioRef.current.srcObject = null }
    pendingIceRef.current = []
    incomingOfferRef.current = null
    callIdRef.current = null
    peerRef.current = null
    setPeer(null)
    setMuted(false)
    setLocalScreenShares([])
    setRemoteScreenShares([])
    setStatusBoth("idle")
  }, [])

  const buildPeerConnection = useCallback(async (to) => {
    const iceServers = await fetchIceServers(authFetch)
    const pc = new RTCPeerConnection({ iceServers })
    pc.onicecandidate = (e) => {
      if (e.candidate) {
        console.debug("[call-diag] local candidate", { to, type: e.candidate.type, protocol: e.candidate.protocol, address: e.candidate.address })
        postSignal(to, "ice", { candidate: e.candidate.toJSON() }).catch(() => cleanup())
      } else {
        console.debug("[call-diag] ICE gathering complete", { to })
      }
    }
    pc.oniceconnectionstatechange = () => {
      const st = pc.iceConnectionState
      console.debug("[call-diag] iceConnectionState", { to, state: st })
      // Fallback for connectionState under/non-reporting (e.g. older Safari): ICE itself
      // reaching connected/completed is sufficient evidence the call is actually live,
      // even if connectionstatechange never separately fires "connected".
      if ((st === "connected" || st === "completed") && statusRef.current !== "idle" && statusRef.current !== "in-call") {
        setStatusBoth("in-call")
      } else if (st === "failed" && statusRef.current !== "idle") {
        setNote({ kind: "failed", peer: peerRef.current }); cleanup()
      }
    }
    pc.onicegatheringstatechange = () => console.debug("[call-diag] iceGatheringState", { to, state: pc.iceGatheringState })
    pc.onsignalingstatechange = () => console.debug("[call-diag] signalingState", { to, state: pc.signalingState })
    pc.ontrack = (e) => {
      console.debug("[call-diag] ontrack", { kind: e.track.kind, from: to, trackId: e.track.id, streamId: e.streams[0]?.id })
      if (e.track.kind === "video") {
        const stream = e.streams[0] || new MediaStream([e.track])
        const id = `${to}:${stream.id}:${e.track.id}`
        setRemoteScreenShares(current => current.some(share => share.id === id)
          ? current
          : [...current, { id, owner: to, stream }])
        e.track.onended = () => setRemoteScreenShares(current => current.filter(share => share.id !== id))
      } else {
        ensureRemoteAudio().srcObject = e.streams[0]
      }
    }
    pc.onconnectionstatechange = () => {
      const st = pc.connectionState
      console.debug("[call-diag] connectionState", { to, state: st })
      if (st === "connected") setStatusBoth("in-call")
      else if (st === "failed" || st === "disconnected" || st === "closed") {
        if (statusRef.current !== "idle") { setNote({ kind: "failed", peer: peerRef.current }); cleanup() }
      }
    }
    pcRef.current = pc
    return pc
  }, [postSignal, cleanup, authFetch])

  const getMic = useCallback(async () => {
    const stream = await navigator.mediaDevices.getUserMedia({ audio: true })
    localStreamRef.current = stream
    return stream
  }, [])

  const startCall = useCallback(async (to) => {
    if (!to || to === username || to.toLowerCase() === "global") return
    if (statusRef.current !== "idle" || groupCallRef.current.status !== "idle") return
    callIdRef.current = newCallId()
    peerRef.current = to
    setPeer(to)
    setStatusBoth("calling")
    try {
      const stream = await getMic()
      const pc = await buildPeerConnection(to)
      stream.getTracks().forEach(t => pc.addTrack(t, stream))
      const offer = await pc.createOffer()
      await pc.setLocalDescription(offer)
      await postSignal(to, "offer", { sdp: pc.localDescription.sdp })
    } catch {
      setNote({ kind: "failed", peer: to })
      cleanup()
    }
  }, [username, getMic, buildPeerConnection, postSignal, cleanup])

  const accept = useCallback(async () => {
    const offer = incomingOfferRef.current
    const to = peerRef.current
    if (!offer || !to) return
    setStatusBoth("connecting")
    try {
      const stream = await getMic()
      const pc = await buildPeerConnection(to)
      stream.getTracks().forEach(t => pc.addTrack(t, stream))
      await pc.setRemoteDescription({ type: "offer", sdp: offer.sdp })
      for (const c of pendingIceRef.current) { try { await pc.addIceCandidate(c) } catch { /* noop */ } }
      pendingIceRef.current = []
      const answer = await pc.createAnswer()
      await pc.setLocalDescription(answer)
      await postSignal(to, "answer", { sdp: pc.localDescription.sdp })
      incomingOfferRef.current = null
    } catch {
      setNote({ kind: "failed", peer: to })
      cleanup()
    }
  }, [getMic, buildPeerConnection, postSignal, cleanup])

  const decline = useCallback(() => {
    if (peerRef.current) postSignal(peerRef.current, "reject", null).catch(() => {})
    cleanup()
  }, [postSignal, cleanup])

  const hangup = useCallback(() => {
    if (peerRef.current) postSignal(peerRef.current, statusRef.current === "calling" ? "cancel" : "end", null).catch(() => {})
    cleanup()
  }, [postSignal, cleanup])

  const toggleMute = useCallback(() => {
    const stream = localStreamRef.current
    if (!stream) return
    const next = !muted
    stream.getAudioTracks().forEach(t => { t.enabled = !next })
    setMuted(next)
  }, [muted])

  const renegotiateScreen = useCallback(async () => {
    const pc = pcRef.current
    const to = peerRef.current
    if (!pc || !to) return
    const offer = await pc.createOffer()
    await pc.setLocalDescription(offer)
    console.debug("[call-diag] sending screen-offer", { to, signalingState: pc.signalingState })
    await postSignal(to, "screen-offer", { sdp: pc.localDescription.sdp })
  }, [postSignal])

  const postGroupSignal = useCallback(async (to, kind, payload) => {
    const response = await authFetch("/api/messenger/call/signal/", {
      method: "POST", headers: { "Content-Type": "application/json" },
      body: JSON.stringify({
        call_id: groupCallIdRef.current, from: username, to, kind,
        payload: { ...(payload || {}), group_id: payload?.group_id || groupCallRef.current.group?.group_id },
      }),
    })
    if (!response.ok) throw new Error(`Group signal failed: ${response.status}`)
    return response
  }, [username, authFetch])

  const renegotiateGroupScreens = useCallback(async () => {
    await Promise.all([...groupPcRefs.current.entries()].map(async ([other, pc]) => {
      const offer = await pc.createOffer()
      await pc.setLocalDescription(offer)
      await postGroupSignal(other, "group-offer", { sdp: pc.localDescription.sdp })
    }))
  }, [postGroupSignal])

  const stopScreenShare = useCallback(async (shareId) => {
    const share = screenSharesRef.current.get(shareId)
    if (!share) return
    screenSharesRef.current.delete(shareId)
    share.stream.getTracks().forEach(track => { track.onended = null; track.stop() })
    if (pcRef.current && share.senders.has("private")) {
      try { pcRef.current.removeTrack(share.senders.get("private")) } catch { /* noop */ }
    }
    groupPcRefs.current.forEach((pc, other) => {
      const sender = share.senders.get(other)
      if (sender) { try { pc.removeTrack(sender) } catch { /* noop */ } }
    })
    setLocalScreenShares(current => current.filter(item => item.id !== shareId))
    try {
      if (statusRef.current === "in-call") await renegotiateScreen()
      else if (groupCallRef.current.status === "in-call") await renegotiateGroupScreens()
    } catch { /* keep audio connected when renegotiation fails */ }
  }, [renegotiateScreen, renegotiateGroupScreens])

  const addScreenShare = useCallback(async () => {
    const privateCall = statusRef.current === "in-call" && pcRef.current
    const group = groupCallRef.current.status === "in-call" && groupPcRefs.current.size > 0
    if ((!privateCall && !group) || !navigator.mediaDevices?.getDisplayMedia) return
    try {
      const stream = await navigator.mediaDevices.getDisplayMedia({ video: true, audio: false })
      const id = stream.id || newCallId()
      const track = stream.getVideoTracks()[0]
      const senders = new Map()
      if (privateCall) senders.set("private", pcRef.current.addTrack(track, stream))
      if (group) groupPcRefs.current.forEach((pc, other) => senders.set(other, pc.addTrack(track, stream)))
      screenSharesRef.current.set(id, { stream, senders })
      setLocalScreenShares(current => [...current, { id, owner: username, stream }])
      track.onended = () => stopScreenShare(id)
      if (privateCall) await renegotiateScreen()
      else await renegotiateGroupScreens()
    } catch { /* user cancelled the browser picker or renegotiation failed */ }
  }, [username, stopScreenShare, renegotiateScreen, renegotiateGroupScreens])

  const clearNote = useCallback(() => setNote(null), [])

  const cleanupGroup = useCallback(() => {
    groupPcRefs.current.forEach(pc => { try { pc.close() } catch { /* noop */ } })
    groupPcRefs.current.clear()
    groupAudioRefs.current.forEach(audio => { audio.srcObject = null; audio.remove() })
    groupAudioRefs.current.clear()
    groupIceRef.current.clear()
    if (groupStreamRef.current) groupStreamRef.current.getTracks().forEach(track => track.stop())
    groupStreamRef.current = null
    screenSharesRef.current.forEach(({ stream }) => stream.getTracks().forEach(track => track.stop()))
    screenSharesRef.current.clear()
    groupCallIdRef.current = null
    setGroupMuted(false)
    setLocalScreenShares([])
    setRemoteScreenShares([])
    setGroupBoth({ status: "idle", group: null, peers: [], pending: [] })
  }, [])

  const ensureGroupStream = useCallback(async () => {
    if (!groupStreamRef.current) groupStreamRef.current = await navigator.mediaDevices.getUserMedia({ audio: true })
    return groupStreamRef.current
  }, [])

  const buildGroupPeer = useCallback(async (other) => {
    if (groupPcRefs.current.has(other)) return groupPcRefs.current.get(other)
    const pc = new RTCPeerConnection({ iceServers: await fetchIceServers(authFetch) })
    const stream = await ensureGroupStream()
    stream.getTracks().forEach(track => pc.addTrack(track, stream))
    screenSharesRef.current.forEach(share => {
      const track = share.stream.getVideoTracks()[0]
      if (track) share.senders.set(other, pc.addTrack(track, share.stream))
    })
    pc.onicecandidate = event => { if (event.candidate) postGroupSignal(other, "group-ice", { candidate: event.candidate.toJSON() }).catch(() => cleanupGroup()) }
    pc.ontrack = event => {
      if (event.track.kind === "video") {
        const remoteStream = event.streams[0] || new MediaStream([event.track])
        const id = `${other}:${remoteStream.id}:${event.track.id}`
        setRemoteScreenShares(current => current.some(share => share.id === id)
          ? current
          : [...current, { id, owner: other, stream: remoteStream }])
        event.track.onended = () => setRemoteScreenShares(current => current.filter(share => share.id !== id))
        return
      }
      let audio = groupAudioRefs.current.get(other)
      if (!audio) { audio = document.createElement("audio"); audio.autoplay = true; audio.style.display = "none"; document.body.appendChild(audio); groupAudioRefs.current.set(other, audio) }
      audio.srcObject = event.streams[0]
    }
    const markConnected = () => {
      if (pc.connectionState === "connected" || ["connected", "completed"].includes(pc.iceConnectionState)) {
        const current = groupCallRef.current
        setGroupBoth({ ...current, status: "in-call", peers: [...new Set([...current.peers, other])], pending: current.pending.filter(name => name !== other) })
      }
    }
    pc.onconnectionstatechange = markConnected
    pc.oniceconnectionstatechange = markConnected
    groupPcRefs.current.set(other, pc)
    return pc
  }, [ensureGroupStream, postGroupSignal, authFetch, cleanupGroup])

  const startGroupCall = useCallback(async (group) => {
    if (!group || statusRef.current !== "idle" || groupCallRef.current.status !== "idle") return
    const members = [...new Set([username, ...(group.members || [])])].filter(Boolean)
    groupCallIdRef.current = newCallId()
    const info = { group_id: group.group_id, name: group.name, members, inviter: username }
    setGroupBoth({ status: "calling", group: info, peers: [], pending: members.filter(member => member !== username) })
    try {
      await ensureGroupStream()
      await Promise.all(members.filter(member => member !== username).map(member => postGroupSignal(member, "group-invite", info)))
    } catch { cleanupGroup() }
  }, [username, ensureGroupStream, postGroupSignal, cleanupGroup])

  const acceptGroupCall = useCallback(async () => {
    const info = groupCallRef.current.group
    if (!info) return
    try {
      await ensureGroupStream()
      setGroupBoth({ ...groupCallRef.current, status: "connecting" })
      await Promise.all(info.members.filter(member => member !== username).map(member => postGroupSignal(member, "group-join", { group_id: info.group_id })))
    } catch { cleanupGroup() }
  }, [username, ensureGroupStream, postGroupSignal, cleanupGroup])

  const leaveGroupCall = useCallback(() => {
    const info = groupCallRef.current.group
    const status = groupCallRef.current.status
    const reason = status === "calling" ? "cancelled" : status === "ringing" ? "declined" : "left"
    if (info) info.members.filter(member => member !== username).forEach(member => postGroupSignal(member, "group-leave", { group_id: info.group_id, reason }).catch(() => {}))
    cleanupGroup()
  }, [username, postGroupSignal, cleanupGroup])

  const toggleGroupMute = useCallback(() => {
    const next = !groupMuted
    groupStreamRef.current?.getAudioTracks().forEach(track => { track.enabled = !next })
    setGroupMuted(next)
  }, [groupMuted])

  // --- incoming-signal poller (always on while logged in) ------------------
  useEffect(() => {
    if (!username) return
    let alive = true

    const handle = async (sig) => {
      const { from, kind, payload, call_id } = sig
      if (kind.startsWith("group-")) {
        if (kind === "group-invite") {
          if (statusRef.current !== "idle" || groupCallRef.current.status !== "idle") {
            const old = groupCallIdRef.current; groupCallIdRef.current = call_id
            await postGroupSignal(from, "group-leave", { group_id: payload?.group_id, reason: "busy" })
            groupCallIdRef.current = old
            return
          }
          groupCallIdRef.current = call_id
          setGroupBoth({ status: "ringing", group: payload, peers: [], pending: (payload?.members || []).filter(member => member !== username) })
          return
        }
        if (call_id !== groupCallIdRef.current) return
        if (kind === "group-join") {
          if (groupCallRef.current.status !== "ringing") {
            try {
              const pc = await buildGroupPeer(from)
              const offer = await pc.createOffer(); await pc.setLocalDescription(offer)
              await postGroupSignal(from, "group-offer", { sdp: pc.localDescription.sdp })
              setGroupBoth({ ...groupCallRef.current, pending: groupCallRef.current.pending.filter(name => name !== from) })
            } catch {
              const current = groupCallRef.current
              const next = { ...current, pending: current.pending.filter(name => name !== from) }
              if (!next.pending.length && !next.peers.length) cleanupGroup()
              else setGroupBoth(next)
            }
          }
        } else if (kind === "group-offer") {
          const pc = await buildGroupPeer(from)
          const collision = pc.signalingState !== "stable"
          const polite = username.localeCompare(from) > 0
          if (collision && !polite) return
          if (collision) await pc.setLocalDescription({ type: "rollback" })
          await pc.setRemoteDescription({ type: "offer", sdp: payload?.sdp })
          for (const candidate of (groupIceRef.current.get(from) || [])) { try { await pc.addIceCandidate(candidate) } catch { /* noop */ } }
          groupIceRef.current.delete(from)
          const answer = await pc.createAnswer(); await pc.setLocalDescription(answer)
          await postGroupSignal(from, "group-answer", { sdp: pc.localDescription.sdp })
        } else if (kind === "group-answer") {
          const pc = groupPcRefs.current.get(from)
          if (pc?.signalingState === "have-local-offer") {
            await pc.setRemoteDescription({ type: "answer", sdp: payload?.sdp })
            for (const candidate of (groupIceRef.current.get(from) || [])) { try { await pc.addIceCandidate(candidate) } catch { /* noop */ } }
            groupIceRef.current.delete(from)
          }
        } else if (kind === "group-ice") {
          const pc = groupPcRefs.current.get(from), candidate = payload?.candidate
          if (candidate && pc?.remoteDescription?.type) { try { await pc.addIceCandidate(candidate) } catch { /* noop */ } }
          else if (candidate) groupIceRef.current.set(from, [...(groupIceRef.current.get(from) || []), candidate])
        } else if (kind === "group-leave") {
          if (payload?.reason === "cancelled" || (groupCallRef.current.status === "ringing" && from === groupCallRef.current.group?.inviter)) {
            cleanupGroup()
            return
          }
          const pc = groupPcRefs.current.get(from)
          if (pc) { try { pc.close() } catch { /* noop */ }; groupPcRefs.current.delete(from) }
          const audio = groupAudioRefs.current.get(from); if (audio) { audio.remove(); groupAudioRefs.current.delete(from) }
          const current = groupCallRef.current
          const next = { ...current, peers: current.peers.filter(peerName => peerName !== from), pending: current.pending.filter(name => name !== from) }
          if (!next.peers.length && !next.pending.length) cleanupGroup()
          else setGroupBoth(next)
        }
        return
      }
      if (kind === "offer") {
        console.debug("[call-diag] received offer", { from, ownStatus: statusRef.current, groupStatus: groupCallRef.current.status })
        // Already busy on another call → tell the caller, ignore.
        if (statusRef.current !== "idle" || groupCallRef.current.status !== "idle") {
          const response = await authFetch("/api/messenger/call/signal/", {
            method: "POST", headers: { "Content-Type": "application/json" },
            body: JSON.stringify({ call_id, from: username, to: from, kind: "busy", payload: null }),
          })
          if (!response.ok) throw new Error(`Busy signal failed: ${response.status}`)
          return
        }
        callIdRef.current = call_id
        peerRef.current = from
        incomingOfferRef.current = { sdp: payload?.sdp }
        setPeer(from)
        setStatusBoth("ringing")
        return
      }
      // Ignore stray signals that don't belong to the active call.
      if (call_id !== callIdRef.current || from !== peerRef.current) return
      if (kind === "answer") {
        const pc = pcRef.current
        console.debug("[call-diag] received answer", { from, hasPc: !!pc, signalingState: pc?.signalingState, pendingIce: pendingIceRef.current.length })
        if (pc) {
          try {
            await pc.setRemoteDescription({ type: "answer", sdp: payload?.sdp })
            for (const c of pendingIceRef.current) { try { await pc.addIceCandidate(c) } catch { /* noop */ } }
            pendingIceRef.current = []
            setStatusBoth("connecting")
          } catch (err) { console.debug("[call-diag] answer handling FAILED", err); setNote({ kind: "failed", peer: from }); cleanup() }
        }
      } else if (kind === "ice") {
        const pc = pcRef.current
        const cand = payload?.candidate
        if (!cand) return
        if (pc && pc.remoteDescription && pc.remoteDescription.type) {
          try { await pc.addIceCandidate(cand) }
          catch (err) { console.debug("[call-diag] addIceCandidate FAILED", { signalingState: pc.signalingState }, err) }
        } else {
          pendingIceRef.current.push(cand)
        }
      } else if (kind === "screen-offer") {
        const pc = pcRef.current
        console.debug("[call-diag] received screen-offer", { from, hasPc: !!pc, signalingState: pc?.signalingState })
        if (!pc) return
        try {
          const collision = pc.signalingState !== "stable"
          const polite = username.localeCompare(from) > 0
          if (collision && !polite) { console.debug("[call-diag] screen-offer dropped: collision + impolite", { signalingState: pc.signalingState }); return }
          if (collision) await pc.setLocalDescription({ type: "rollback" })
          await pc.setRemoteDescription({ type: "offer", sdp: payload?.sdp })
          const answer = await pc.createAnswer()
          await pc.setLocalDescription(answer)
          console.debug("[call-diag] sending screen-answer", { to: from })
          await postSignal(from, "screen-answer", { sdp: pc.localDescription.sdp })
        } catch (err) { console.debug("[call-diag] screen-offer handling FAILED", err) }
      } else if (kind === "screen-answer") {
        const pc = pcRef.current
        console.debug("[call-diag] received screen-answer", { from, signalingState: pc?.signalingState })
        if (pc?.signalingState === "have-local-offer") {
          try { await pc.setRemoteDescription({ type: "answer", sdp: payload?.sdp }) }
          catch (err) { console.debug("[call-diag] screen-answer setRemoteDescription FAILED", err) }
        }
      } else if (kind === "reject") {
        setNote({ kind: "declined", peer: from }); cleanup()
      } else if (kind === "busy") {
        setNote({ kind: "busy", peer: from }); cleanup()
      } else if (kind === "cancel" || kind === "end") {
        setNote({ kind: "ended", peer: from }); cleanup()
      }
    }

    const poll = async () => {
      try {
        const r = await authFetch("/api/messenger/call/poll/?username=" + encodeURIComponent(username))
        const d = await r.json()
        for (const sig of (d.signals || [])) {
          if (!alive) break
          try { await handle(sig) } catch { /* one malformed or raced signal must not drop the rest of the batch */ }
        }
      } catch { /* network blip — retry next tick */ }
    }

    const id = setInterval(poll, POLL_MS)
    poll()
    return () => { alive = false; clearInterval(id) }
  }, [username, cleanup, cleanupGroup, buildGroupPeer, postGroupSignal, authFetch])

  useEffect(() => () => { cleanup(); cleanupGroup() }, [cleanup, cleanupGroup])

  // --- audible ring -------------------------------------------------------
  // A looping WebAudio tone so an incoming call is noticed even when the chat
  // overlay is closed: double-ring while `ringing` (incoming), a slower single
  // ringback while `calling` (outgoing). Silent in every other state.
  useEffect(() => {
    if (status !== "ringing" && status !== "calling") return
    const incoming = status === "ringing"
    let ctx, timer, stopped = false
    try {
      ctx = new (window.AudioContext || window.webkitAudioContext)()
      if (ctx.state === "suspended") ctx.resume().catch(() => {})
      const tone = (freq, start, dur) => {
        const t0 = ctx.currentTime + start
        const osc = ctx.createOscillator()
        const gain = ctx.createGain()
        osc.connect(gain); gain.connect(ctx.destination)
        osc.type = "sine"; osc.frequency.value = freq
        gain.gain.setValueAtTime(0.0001, t0)
        gain.gain.exponentialRampToValueAtTime(0.18, t0 + 0.02)
        gain.gain.exponentialRampToValueAtTime(0.0001, t0 + dur)
        osc.start(t0); osc.stop(t0 + dur + 0.03)
      }
      const ring = () => {
        if (stopped) return
        if (incoming) { tone(880, 0, 0.4); tone(660, 0.55, 0.4) }
        else { tone(440, 0, 0.5) }
      }
      ring()
      timer = setInterval(ring, incoming ? 2200 : 3200)
    } catch { /* no audio available */ }
    return () => {
      stopped = true
      if (timer) clearInterval(timer)
      if (ctx) setTimeout(() => { try { ctx.close() } catch { /* noop */ } }, 120)
    }
  }, [status])

  useEffect(() => {
    if (groupCall.status !== "ringing") return
    let ctx, timer, stopped = false
    try {
      ctx = new (window.AudioContext || window.webkitAudioContext)()
      const ring = () => {
        if (stopped) return
        const oscillator = ctx.createOscillator(), gain = ctx.createGain()
        oscillator.connect(gain); gain.connect(ctx.destination)
        oscillator.frequency.value = 740
        gain.gain.setValueAtTime(0.0001, ctx.currentTime)
        gain.gain.exponentialRampToValueAtTime(0.16, ctx.currentTime + 0.02)
        gain.gain.exponentialRampToValueAtTime(0.0001, ctx.currentTime + 0.45)
        oscillator.start(); oscillator.stop(ctx.currentTime + 0.48)
      }
      ring(); timer = setInterval(ring, 2200)
    } catch { /* no audio available */ }
    return () => {
      stopped = true
      if (timer) clearInterval(timer)
      if (ctx) setTimeout(() => { try { ctx.close() } catch { /* noop */ } }, 120)
    }
  }, [groupCall.status])

  return {
    status, peer, muted, note, localScreenShares, remoteScreenShares,
    startCall, accept, decline, hangup, toggleMute, addScreenShare, stopScreenShare, clearNote,
    groupCall, groupMuted, startGroupCall, acceptGroupCall, leaveGroupCall, toggleGroupMute,
  }
}
